import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import StarIcon from "@mui/icons-material/Star";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import LocalDiningIcon from "@mui/icons-material/LocalDining";

import ApiServices from "../../Utils/ApiServices";

const getDisplayPrice = (item, serviceType, selectedVariantId = null) => {
  if (selectedVariantId && Array.isArray(item?.variant_groups)) {
    for (const group of item.variant_groups) {
      const found = group?.variants?.find((v) => v.id === selectedVariantId);
      if (found) return found?.pricing?.[serviceType] ?? null;
    }
  }

  if (item?.pricing) return item.pricing?.[serviceType] ?? null;

  if (Array.isArray(item?.variant_groups)) {
    const firstAvailableVariant = item.variant_groups
      .flatMap((group) => group.variants || [])
      .find((variant) => variant.is_available);

    return firstAvailableVariant?.pricing?.[serviceType] ?? null;
  }

  return null;
};


const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.04,
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1],
    },
  }),
};

const getErrorText = (error, fallback) =>
  error?.response?.data?.detail ||
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const MenuItemSections = ({
  sections,
  brand,
  serviceType,
  customerId,
  token,
  isLoggedIn,
  selectedBranchId,
  refreshCart,
  showMessage,
}) => {
  const [variantDialog, setVariantDialog] = useState({
    open: false,
    item: null,
    selectedVariantId: null,
    selectedVariantName: "",
    quantity: 1,
    notes: "",
  });

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const openVariantDialog = (item) => {
    let defaultVariantId = null;
    let defaultVariantName = "";

    if (Array.isArray(item?.variant_groups) && item.variant_groups.length) {
      const firstGroup = item.variant_groups[0];
      const firstAvailable =
        firstGroup?.variants?.find((variant) => variant.is_available) ||
        firstGroup?.variants?.[0];

      if (firstAvailable) {
        defaultVariantId = firstAvailable.id;
        defaultVariantName = firstAvailable.name;
      }
    }

    setVariantDialog({
      open: true,
      item,
      selectedVariantId: defaultVariantId,
      selectedVariantName: defaultVariantName,
      quantity: 1,
      notes: "",
    });
  };

  const closeVariantDialog = () => {
    setVariantDialog({
      open: false,
      item: null,
      selectedVariantId: null,
      selectedVariantName: "",
      quantity: 1,
      notes: "",
    });
  };

  const updateCartMeta = async () => {
    const cartId = localStorage.getItem("cart_id");
    if (!cartId || !customerId) return;

    try {
      await ApiServices.patch(
        `/api/v1/customers/${customerId}/cart/${cartId}/`,
        {
          service_type: serviceType,
          location_id: selectedBranchId,
        },
        authHeaders
      );
    } catch {}
  };

  const addToCart = async () => {
    if (!isLoggedIn) {
      showMessage("Please login or signup to add items to cart.", "warning");
      return;
    }

    if (!customerId) {
      showMessage("Customer id not found in session storage.", "error");
      return;
    }

    const item = variantDialog.item;
    if (!item) return;

    try {
      await ApiServices.post(
        `/api/v1/customers/${customerId}/cart/items/`,
        {
          item_id: item.id,
          category_id: item.category_id,
          item_name: item.name,
          variant_id: variantDialog.selectedVariantId || null,
          variant_name: variantDialog.selectedVariantName || "",
          quantity: Number(variantDialog.quantity || 1),
          notes: variantDialog.notes || "",
        },
        authHeaders
      );

      // await updateCartMeta();
      await refreshCart();
      closeVariantDialog();
      showMessage("Item added to cart.", "success");
    } catch (error) {
      showMessage(getErrorText(error, "Failed to add item to cart."), "error");
    }
  };

  const renderItemCard = (item, categoryId, index) => {
    const displayPrice = getDisplayPrice(item, serviceType);
    const hasVariants = item?.variant_groups?.some(
      (group) => Array.isArray(group.variants) && group.variants.length > 0
    );

    const imageUrl =
      item.image ||
      "https://images.unsplash.com/photo-1513104890138-7c749659a591?q=80&w=1200&auto=format&fit=crop";

    return (
      <Grid item xs={12} sm={6} lg={4} key={item.id} sx={{ display: "flex" }}>
          <Card
            sx={{
              height: "100%",
              width: "100%",
              borderRadius: 5,
              overflow: "hidden",
              background: "linear-gradient(180deg, #ffffff 0%, #FFF8F0 100%)",
              border: "1px solid rgba(183,28,28,0.08)",
              boxShadow: "0 14px 40px rgba(122,14,14,0.10)",
            }}
          >
            <Box sx={{ position: "relative", height: 220, overflow: "hidden" }}>
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url("${imageUrl}")`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, transparent 40%, rgba(42,10,10,0.55) 100%)",
                }}
              />

              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: "absolute",
                  top: 12,
                  left: 12,
                  right: 12,
                  zIndex: 2,
                }}
              >
                <Chip
                  size="small"
                  label={item.is_veg ? "Veg" : "Non-Veg"}
                  sx={{
                    bgcolor: "rgba(255,255,255,0.95)",
                    color: brand.charcoal,
                    fontWeight: 700,
                    border: `1px solid ${item.is_veg ? "#16a34a" : "#dc2626"}`,
                  }}
                />

                {item.badge ? (
                  <Chip
                    size="small"
                    label={item.badge}
                    sx={{
                      background: brand.gradientAmber,
                      color: brand.redDeep,
                      fontWeight: 800,
                    }}
                  />
                ) : null}
              </Stack>

              {displayPrice !== null && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    zIndex: 2,
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 999,
                    background: brand.gradientPrimary,
                    color: "#fff",
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  {hasVariants ? "from " : ""}${Number(displayPrice).toFixed(2)}
                </Box>
              )}
            </Box>

            <CardContent sx={{ p: 2.5 }}>
              <Stack spacing={1.5}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 800,
                      color: brand.charcoal,
                      lineHeight: 1.15,
                    }}
                  >
                    {item.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: 0.6,
                      minHeight: 38,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {item.description || "Freshly prepared, made with love."}
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    icon={
                      <StarIcon
                        sx={{
                          fontSize: "16px !important",
                          color: "#FFB300 !important",
                        }}
                      />
                    }
                    size="small"
                    label={`${item.rating || "0"} (${item.rating_count || 0})`}
                    sx={{ fontWeight: 700, bgcolor: "#FFF8E1" }}
                  />

                  <Chip
                    icon={<AccessTimeIcon sx={{ fontSize: "16px !important" }} />}
                    size="small"
                    label={`${item.prep_time_minutes || 0} min`}
                    sx={{ bgcolor: "#FFF3E0" }}
                  />

                  <Chip
                    icon={
                      <LocalFireDepartmentIcon
                        sx={{
                          fontSize: "16px !important",
                          color: "#E64A19 !important",
                        }}
                      />
                    }
                    size="small"
                    label={`${item.calories || 0} cal`}
                    sx={{ bgcolor: "#FFEBE5" }}
                  />
                </Stack>

                {!!item.tags?.length && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {item.tags.slice(0, 3).map((tag) => (
                      <Chip
                        key={tag}
                        size="small"
                        label={tag}
                        sx={{
                          bgcolor: "rgba(183,28,28,0.06)",
                          color: brand.redDeep,
                          fontWeight: 600,
                        }}
                      />
                    ))}
                  </Stack>
                )}

                {!!item.ingredients?.length && (
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {item.ingredients.slice(0, 4).map((ingredient) => (
                      <Chip
                        key={ingredient}
                        size="small"
                        label={ingredient}
                        variant="outlined"
                        sx={{
                          fontSize: 11,
                          height: 22,
                          color: "text.secondary",
                        }}
                      />
                    ))}
                  </Stack>
                )}

                <Divider
                  sx={{
                    borderStyle: "dashed",
                    borderColor: "rgba(183,28,28,0.15)",
                  }}
                />

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ letterSpacing: 1 }}
                    >
                      {hasVariants ? "STARTS FROM" : "PRICE"}
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 900,
                        background: brand.gradientPrimary,
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        lineHeight: 1.1,
                      }}
                    >
                      {displayPrice !== null ? `$${Number(displayPrice).toFixed(2)}` : "—"}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    disabled={!item.is_available}
                    startIcon={<AddShoppingCartIcon />}
                    onClick={() => openVariantDialog({ ...item, category_id: categoryId })}
                    sx={{
                      borderRadius: 999,
                      fontWeight: 800,
                      px: 2.4,
                      py: 1,
                      textTransform: "none",
                      color: "#fff",
                      background: brand.gradientPrimary,
                      boxShadow: "0 10px 24px rgba(183,28,28,0.4)",
                      "&:hover": { background: brand.gradientPrimary },
                    }}
                  >
                    {hasVariants ? "Select" : "Add"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
      </Grid>
    );
  };

  if (!sections.length) {
    return (
      <Alert severity="info" sx={{ borderRadius: 4, fontWeight: 600 }}>
        No menu items match your search or filters.
      </Alert>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        {sections.map((section) => (
          <Box key={section.id}>
            <Accordion
              defaultExpanded
              sx={{
                borderRadius: "20px !important",
                overflow: "hidden",
                border: "1px solid rgba(183,28,28,0.08)",
                boxShadow: "0 14px 36px rgba(122,14,14,0.08)",
                "&:before": { display: "none" },
                bgcolor: "#fff",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff", fontSize: 30 }} />}
                sx={{
                  background: section.is_veg
                    ? "linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #43A047 100%)"
                    : brand.gradientPrimary,
                  color: "#fff",
                  minHeight: 88,
                  px: 3,
                }}
              >
                <Stack direction="row" spacing={2} alignItems="center">
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: brand.gradientAmber,
                    }}
                  >
                    <RestaurantMenuIcon sx={{ color: brand.redDeep, fontSize: 28 }} />
                  </Box>

                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 800,
                        fontSize: { xs: 22, md: 28 },
                        lineHeight: 1.1,
                      }}
                    >
                      {section.name}
                    </Typography>

                    <Stack direction="row" spacing={1} sx={{ mt: 0.6 }}>
                      <Chip
                        size="small"
                        label={section.is_veg ? "Pure Veg" : "Non-Veg"}
                        sx={{
                          fontWeight: 700,
                          bgcolor: "rgba(255,255,255,0.18)",
                          color: "#fff",
                        }}
                      />

                      <Chip
                        size="small"
                        icon={
                          <LocalDiningIcon
                            sx={{
                              fontSize: "14px !important",
                              color: "#fff !important",
                            }}
                          />
                        }
                        label={`${section.categories?.length || 0} Categories`}
                        sx={{
                          fontWeight: 700,
                          bgcolor: "rgba(255,255,255,0.18)",
                          color: "#fff",
                        }}
                      />
                    </Stack>
                  </Box>
                </Stack>
              </AccordionSummary>

              <AccordionDetails sx={{ p: { xs: 2, md: 4 }, bgcolor: "#FFFCF7" }}>
                <Stack spacing={5}>
                  {(section.categories || []).map((category) => (
                    <Box key={category.id}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 2.5 }}
                      >
                        <Box sx={{ position: "relative", pl: 2.5 }}>
                          <Box
                            sx={{
                              position: "absolute",
                              left: 0,
                              top: 4,
                              bottom: 4,
                              width: 5,
                              borderRadius: 5,
                              background: brand.gradientAmber,
                            }}
                          />

                          <Typography
                            sx={{
                              fontFamily: "'Playfair Display', Georgia, serif",
                              fontWeight: 800,
                              fontSize: { xs: 20, md: 24 },
                              color: brand.charcoal,
                              lineHeight: 1.1,
                            }}
                          >
                            {category.name}
                          </Typography>

                          {category.description ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                              {category.description}
                            </Typography>
                          ) : null}
                        </Box>

                        <Chip
                          label={`${category.items?.length || 0} items`}
                          sx={{
                            bgcolor: "#FFF3E0",
                            color: brand.redDeep,
                            fontWeight: 700,
                          }}
                        />
                      </Stack>

                      <Grid container spacing={3}>
                        {(category.items || []).map((item, index) =>
                          renderItemCard(item, category.id, index)
                        )}
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Stack>

      <Dialog
        open={variantDialog.open}
        onClose={closeVariantDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 5,
            overflow: "hidden",
            background: "linear-gradient(180deg, #ffffff 0%, #FFFAF3 100%)",
          },
        }}
      >
        <Box sx={{ background: brand.gradientPrimary, color: "#fff", px: 3, py: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography
                sx={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 800,
                  fontSize: 24,
                  lineHeight: 1.1,
                }}
              >
                {variantDialog.item?.name || "Add Item"}
              </Typography>

              <Typography variant="caption" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                CUSTOMIZE YOUR ORDER
              </Typography>
            </Box>

            <IconButton onClick={closeVariantDialog} sx={{ color: "#fff" }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </Box>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            {variantDialog.item?.description ? (
              <Typography variant="body2" color="text.secondary">
                {variantDialog.item.description}
              </Typography>
            ) : null}

            {!!variantDialog.item?.variant_groups?.length &&
              variantDialog.item.variant_groups.map((group) => (
                <Box key={group.id}>
                  <Typography
                    variant="overline"
                    sx={{ fontWeight: 800, color: brand.red, letterSpacing: 2 }}
                  >
                    {group.name}
                  </Typography>

                  <FormControl fullWidth sx={{ mt: 0.5 }}>
                    <Select
                      value={variantDialog.selectedVariantId || ""}
                      onChange={(event) => {
                        const selected = group.variants.find(
                          (variant) => variant.id === event.target.value
                        );

                        setVariantDialog((prev) => ({
                          ...prev,
                          selectedVariantId: event.target.value,
                          selectedVariantName: selected?.name || "",
                        }));
                      }}
                      sx={{ borderRadius: 999, bgcolor: "#fff" }}
                    >
                      {(group.variants || []).map((variant) => (
                        <MenuItem
                          key={variant.id}
                          value={variant.id}
                          disabled={!variant.is_available}
                        >
                          {variant.name} — $
                          {Number(variant?.pricing?.[serviceType] ?? 0).toFixed(2)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ))}

            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: brand.red, letterSpacing: 2 }}
              >
                Quantity
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 0.5,
                  bgcolor: "#fff",
                  borderRadius: 999,
                  px: 1,
                  py: 0.5,
                  border: "1px solid rgba(183,28,28,0.2)",
                  width: "fit-content",
                }}
              >
                <IconButton
                  onClick={() =>
                    setVariantDialog((prev) => ({
                      ...prev,
                      quantity: Math.max(1, Number(prev.quantity) - 1),
                    }))
                  }
                >
                  <RemoveRoundedIcon />
                </IconButton>

                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 20,
                    minWidth: 32,
                    textAlign: "center",
                  }}
                >
                  {variantDialog.quantity}
                </Typography>

                <IconButton
                  onClick={() =>
                    setVariantDialog((prev) => ({
                      ...prev,
                      quantity: Number(prev.quantity) + 1,
                    }))
                  }
                  sx={{
                    color: "#fff",
                    background: brand.gradientPrimary,
                    "&:hover": { background: brand.gradientPrimary, opacity: 0.9 },
                  }}
                >
                  <AddRoundedIcon />
                </IconButton>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="overline"
                sx={{ fontWeight: 800, color: brand.red, letterSpacing: 2 }}
              >
                Special Notes
              </Typography>

              <TextField
                placeholder="Extra cheese, no onion, less spicy..."
                value={variantDialog.notes}
                onChange={(event) =>
                  setVariantDialog((prev) => ({
                    ...prev,
                    notes: event.target.value,
                  }))
                }
                fullWidth
                multiline
                minRows={3}
                sx={{
                  mt: 0.5,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 3,
                    bgcolor: "#fff",
                  },
                }}
              />
            </Box>

            <Card
              sx={{
                borderRadius: 4,
                background: brand.gradientHero,
                color: "#fff",
              }}
            >
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 2 }}>
                      TOTAL · {variantDialog.quantity}×
                    </Typography>

                    <Typography
                      sx={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontWeight: 900,
                        fontSize: 32,
                        lineHeight: 1,
                        mt: 0.5,
                      }}
                    >
                      $
                      {(
                        Number(
                          getDisplayPrice(
                            variantDialog.item,
                            serviceType,
                            variantDialog.selectedVariantId
                          ) || 0
                        ) * Number(variantDialog.quantity || 1)
                      ).toFixed(2)}
                    </Typography>
                  </Box>

                  <LocalFireDepartmentIcon
                    sx={{ fontSize: 56, color: brand.amber, opacity: 0.85 }}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeVariantDialog}
            sx={{
              borderRadius: 999,
              fontWeight: 700,
              textTransform: "none",
              px: 3,
              borderColor: "rgba(183,28,28,0.3)",
              color: brand.red,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={addToCart}
            startIcon={<AddShoppingCartIcon />}
            sx={{
              borderRadius: 999,
              fontWeight: 800,
              textTransform: "none",
              px: 3,
              py: 1.1,
              background: brand.gradientPrimary,
              "&:hover": { background: brand.gradientPrimary },
            }}
          >
            Add to Cart
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MenuItemSections;
